import React, { Component, createContext, useContext } from 'react'
import { jwtDecode } from 'jwt-decode'

// Context for tenant configuration
const TenantContext = createContext()

// Tenant Provider Class Component
class TenantProvider extends Component {
  constructor(props) {
    super(props)
    this.state = {
      tenantConfigs: [],
      currentTenant: null,
      config: null,
      isLoading: true,
      error: null,
      user: JSON.parse(localStorage.getItem('user')),
      isAuthenticated: !!localStorage.getItem('token'),
      baseUrl: 'http://localhost:3001'
    }
  }

  componentDidMount() {
    if (!this.state.isAuthenticated) {
      this.redirectToLogin()
      return
    }
    this.fetchTenantConfigs()
  }

  redirectToLogin = () => {
    // Use setTimeout to ensure this runs after render
    setTimeout(() => {
      window.location.href = '/login'
    }, 0)
  }

  checkLogin = () => {
    const token = localStorage.getItem('token')
    if (!token) {
      this.setState({ isAuthenticated: false }, this.redirectToLogin)
    }
  }

  fetchTenantConfigs = async () => {
    this.setState({ isLoading: true, error: null })
    if (!this.state.user) {
      this.setState({ isLoading: false, error: null })
      return
    }
    try {
      const token = localStorage.getItem('token')
      const email = this.state.user.email

      const response = await fetch(this.getInitialApiUrl('auth', 'tenants'), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ email })
      })
      const data = await response.json()

      if (!response.ok) {
        throw new Error(data.message || 'Failed to fetch tenant configurations')
      }

      const tenantConfigs = data.res || []

      if (tenantConfigs.length === 0) {
        throw new Error('No tenant configurations available')
      }

      const initialTenant = this.getInitialTenant(tenantConfigs)
      const initialConfig = this.getTenantConfig(tenantConfigs, initialTenant)

      this.setState(
        {
          tenantConfigs: tenantConfigs,
          currentTenant: initialTenant,
          config: initialConfig,
          isLoading: false,
          // baseUrl: initialConfig.api.baseUrl,
        },
        () => {
          this.applyTenantConfiguration()
          // The UI tenant is restored from localStorage / the ?tenant= param,
          // but the JWT still carries whatever tenant was issued at login.
          // Without this, the branding says one tenant while every API call is
          // scoped to another. Re-issue the token so the two agree.
          this.syncTokenTenant(initialTenant)
        }
      )
    } catch (error) {
      this.setState({
        error: error.message,
        isLoading: false
      })
    }
  }

  getTokenTenant = () => {
    try {
      const token = localStorage.getItem('token')
      return token ? jwtDecode(token).tenantId : null
    } catch (err) {
      return null
    }
  }

  syncTokenTenant = async tenantId => {
    if (!tenantId) return
    if (this.getTokenTenant() === tenantId) return
    await this.switchTenant(tenantId)
  }

  getInitialApiUrl = (endpoint, sub = '') => {
    const endpoints = {
      // Injected by webpack DefinePlugin (see webpack.*.config.js).
      // Local dev: '' -> relative URLs, proxied to the local API by the dev server.
      // Production: falls back to the deployed API host.
      baseUrl:
        typeof process.env.API_BASE_URL === 'string'
          ? process.env.API_BASE_URL
          : 'https://airs-api.hiringgo.com',
      auth: {
        signin: '/api/v1/login',
        signup: '/api/v1/signup',
        tenants: '/api/v1/tenants',
        users: '/api/v1/recruiters',
        switchTenant: '/api/v1/switch-tenant'
      }
    }

    if (sub !== '') {
      const main = endpoints[endpoint]
      if (main && typeof main === 'object' && sub in main) {
        return `${endpoints.baseUrl}${main[sub]}`
      }
    }

    return `${endpoints.baseUrl}${endpoint}`
  }

  getTenantConfig = (configs, tenantId) => {
    return configs.find(tenant => tenant.id === tenantId) || configs[0]
  }

  getInitialTenant = configs => {
    // Priority: URL param > localStorage > default (first config)
    const urlParams = new URLSearchParams(window.location.search)
    const urlTenant = urlParams.get('tenant')
    const savedTenant = localStorage.getItem('currentTenant')

    if (urlTenant && configs.some(t => t.id === urlTenant)) {
      return urlTenant
    }
    if (savedTenant && configs.some(t => t.id === savedTenant)) {
      return savedTenant
    }
    return configs[0].id || null
  }

  applyTenantConfiguration = () => {
    const { config } = this.state
    if (!config) return

    // Apply CSS variables
    const root = document.documentElement
    Object.entries(config.theme).forEach(([key, value]) => {
      root.style.setProperty(`--color-${key}`, value)
    })

    // Update favicon
    this.updateFavicon(config.branding.favicon)

    // Update page title
    document.title = config.branding.companyName

    // Update meta theme color
    this.updateMetaThemeColor(config.theme.primary)

    // Save to localStorage
    localStorage.setItem('currentTenant', this.state.currentTenant)
    localStorage.setItem('tenantConfig', JSON.stringify(config))
  }

  updateFavicon = faviconUrl => {
    let favicon = document.querySelector('link[rel="icon"]')
    if (!favicon) {
      favicon = document.createElement('link')
      favicon.rel = 'icon'
      document.head.appendChild(favicon)
    }
    favicon.href = faviconUrl
  }

  updateMetaThemeColor = color => {
    let metaTheme = document.querySelector('meta[name="theme-color"]')
    if (!metaTheme) {
      metaTheme = document.createElement('meta')
      metaTheme.name = 'theme-color'
      document.head.appendChild(metaTheme)
    }
    metaTheme.content = color
  }

  switchTenant = async tenantId => {
    const { tenantConfigs } = this.state
    const tenantConfig = this.getTenantConfig(tenantConfigs, tenantId)

    if (!tenantConfig) {
      this.setState({ error: `Tenant ${tenantId} not found` })
      return
    }

    this.setState({ isLoading: true, error: null })

    try {
      const token = localStorage.getItem('token');
      const response = await fetch(this.getInitialApiUrl('auth', 'switchTenant'), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({ tenantId })
      });
      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || 'Failed to switch tenant on the server');
      }

      if (data.response && data.response.token) {
        localStorage.setItem('token', data.response.token);
        localStorage.setItem('user', JSON.stringify(data.response.user));
        this.setState({ user: data.response.user });
      }

      this.setState(
        {
          currentTenant: tenantId,
          config: tenantConfig,
          isLoading: false
        },
        () => {
          this.applyTenantConfiguration()
        }
      )

      // Update URL without page reload
      const url = new URL(window.location)
      // url.searchParams.set('tenant', tenantId);
      window.history.replaceState({}, '', url)
    } catch (error) {
      this.setState({
        error: error.message,
        isLoading: false
      })
    }
  }

  validateTenantAccess = async tenantId => {
    // Simulate API call to check if user has access to this tenant
    return new Promise((resolve, reject) => {
      setTimeout(() => {
        // Simulate some tenants requiring special access
        if (tenantId === 'allzone') {
          const hasAccess = localStorage.getItem('allzoneAccess') === 'true'
          if (!hasAccess) {
            reject(new Error('Access denied to Allzone'))
            return
          }
        }
        resolve()
      }, 500)
    })
  }

  getApiUrl = (endpoint, sub = '') => {
    const { config } = this.state
    if (!config.api) return ''

    const baseUrl = config.api.baseUrl
    // const baseUrl = 'http://localhost:3000'
    const endpoints = config.api.endpoints

    // Handle nested endpoint: e.g., endpoint = "auth", sub = "signin"
    if (sub !== '') {
      const main = endpoints[endpoint]
      if (main && typeof main === 'object' && sub in main) {
        return `${baseUrl}${main[sub]}`
      }
    }

    // Handle flat endpoint: e.g., endpoint = "candidate"
    const flatPath = endpoints[endpoint]
    if (typeof flatPath === 'string') {
      return `${baseUrl}${flatPath}`
    }

    return `${baseUrl}${endpoint}`
  }

  getOaiApiUrl = (endpoint, sub = '') => {
    const { config } = this.state
    if (!config.api) return ''

    const baseUrl = config.api.oaiUrl
    const endpoints = config.api.endpoints

    // Handle nested endpoint: e.g., endpoint = "auth", sub = "signin"
    if (sub !== '') {
      const main = endpoints[endpoint]
      if (main && typeof main === 'object' && sub in main) {
        return `${baseUrl}${main[sub]}`
      }
    }

    // Handle flat endpoint: e.g., endpoint = "candidate"
    const flatPath = endpoints[endpoint]
    if (typeof flatPath === 'string') {
      return `${baseUrl}${flatPath}`
    }

    // Fallback: return base + endpoint (assumes endpoint is a path)
    return `${baseUrl}/${endpoint}`
  }

  getApiHeaders = () => {
    const { currentTenant } = this.state
    const token = localStorage.getItem(`authToken_${currentTenant}`)
    return {
      'Content-Type': 'application/json',
      'X-Tenant-ID': currentTenant,
      ...(token && { Authorization: `Bearer ${token}` })
    }
  }

  makeApiCall = async (endpoint, options = {}) => {
    const { config } = this.state
    if (!config.api) {
      throw new Error('Tenant configuration not loaded')
    }

    const url = this.getApiUrl(endpoint)
    const headers = this.getApiHeaders()

    const requestOptions = {
      ...options,
      headers: { ...headers, ...options.headers },
      timeout: config.api.timeout
    }

    // Add retry logic
    let lastError
    for (let attempt = 0; attempt < config.api.retryAttempts; attempt++) {
      try {
        const response = await fetch(url, requestOptions)
        if (!response.ok) {
          throw new Error(`HTTP ${response.status}: ${response.statusText}`)
        }
        return await response.json()
      } catch (error) {
        lastError = error
        if (attempt < config.api.retryAttempts - 1) {
          await new Promise(resolve =>
            setTimeout(resolve, 1000 * (attempt + 1))
          )
        }
      }
    }
    throw lastError
  }

  getCompanyName = () => {
    const { tenantConfigs, currentTenant } = this.state
    return tenantConfigs.map(tenant => ({
      name: tenant.branding.companyName,
      val: tenant.id,
      active: tenant.id === currentTenant
    }))
  }

  getAvailableTenants = () => {
    const { tenantConfigs } = this.state
    return tenantConfigs.map(tenant => tenant.id)
  }

  render() {
    const { isLoading, error } = this.state

    if (isLoading) {
      return <div>Loading tenant configurations...</div>
    }

    if (error) {
      return <div>Error loading tenant configurations: {error}</div>
    }

    if (!this.state.config) {
      return <div>No tenant configuration available</div>
    }

    const contextValue = {
      tenants: this.state.tenantConfigs,
      user: this.state.user,
      currentTenant: this.state.currentTenant,
      config: this.state.config,
      isLoading: this.state.isLoading,
      error: this.state.error,
      switchTenant: this.switchTenant,
      getApiUrl: this.getApiUrl,
      getInitialApiUrl: this.getInitialApiUrl,
      getOaiApiUrl: this.getOaiApiUrl,
      getApiHeaders: this.getApiHeaders,
      makeApiCall: this.makeApiCall,
      availableTenants: this.getAvailableTenants(),
      getCompanyName: this.getCompanyName,
      refreshTenants: this.fetchTenantConfigs,
      baseUrl: this.state.baseUrl
    }

    return (
      <TenantContext.Provider value={contextValue}>
        {this.props.children}
      </TenantContext.Provider>
    )
  }
}

const useTenant = () => useContext(TenantContext)

// HOC for class components to access tenant context
const withTenant = WrappedComponent => {
  return class extends Component {
    render() {
      return (
        <TenantContext.Consumer>
          {tenantContext => (
            <WrappedComponent {...this.props} tenant={tenantContext} />
          )}
        </TenantContext.Consumer>
      )
    }
  }
}

export { TenantProvider, withTenant, useTenant }
